import { useState, useCallback, useRef, useEffect } from "react";
import { Camera, Upload, X, Video, Link, AlertCircle, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import logger from "../../utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Photo categories with icons and labels
const PHOTO_CATEGORIES = [
  { id: 'front_of_house', label: 'Front of House', icon: '🏠' },
  { id: 'back_of_house', label: 'Back of House', icon: '🏡' },
  { id: 'left_side', label: 'Left Side', icon: '➡️' },
  { id: 'right_side', label: 'Right Side', icon: '⬅️' },
  { id: 'living_room', label: 'Living Room', icon: '🛋️' },
  { id: 'kitchen', label: 'Kitchen', icon: '🍳' },
  { id: 'bedrooms', label: 'Bedrooms', icon: '🛏️' },
  { id: 'bathrooms', label: 'Bathrooms', icon: '🚿' },
  { id: 'garage', label: 'Garage', icon: '🚗' },
  { id: 'basement_crawl_space', label: 'Basement/Crawl Space', icon: '🏚️' },
  { id: 'mechanical_room', label: 'Mechanical Room', icon: '🔧' },
  { id: 'damaged_areas', label: 'Damaged Areas', icon: '⚠️' },
  { id: 'other', label: 'Other Photos', icon: '📷' },
];

const MAX_PHOTOS = 30;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];

// PhotoUploader sub-component
const CategoryUploader = ({ 
  category, 
  photos, 
  onUpload, 
  onRemove, 
  isUploading,
  sessionId 
}) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  
  const categoryPhotos = photos.filter(p => p.category === category.id);
  
  const handleFileSelect = async (files) => {
    const fileList = Array.from(files);
    for (const file of fileList) {
      await onUpload(file, category.id);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = () => {
    setDragOver(false);
  };
  
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4 hover:border-[#D4AF37]/30 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{category.icon}</span>
          <span className="text-white font-medium text-sm">{category.label}</span>
        </div>
        <span className="text-[#666] text-xs">
          {categoryPhotos.length} photo{categoryPhotos.length !== 1 ? 's' : ''}
        </span>
      </div>
      
      {/* Photo previews */}
      {categoryPhotos.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {categoryPhotos.map((photo) => (
            <div 
              key={photo.id} 
              className="relative w-16 h-16 border border-white/10 overflow-hidden group"
            >
              <img 
                src={photo.previewUrl || `${API}/photos/${photo.storage_path?.replace('knotts-investments/photos/', '')}`}
                alt={photo.filename}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => onRemove(photo.id)}
                className="absolute top-0 right-0 w-5 h-5 bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                type="button"
              >
                <X className="w-3 h-3 text-white" />
              </button>
              {photo.uploading && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed p-3 text-center cursor-pointer transition-colors ${
          dragOver 
            ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
            : 'border-white/10 hover:border-[#D4AF37]/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.heic,.heif"
          onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
          className="hidden"
        />
        <Upload className={`w-5 h-5 mx-auto mb-1 ${dragOver ? 'text-[#D4AF37]' : 'text-[#666]'}`} />
        <p className="text-[#666] text-xs">Drop or click to add</p>
      </div>
    </div>
  );
};

// Video uploader sub-component
const VideoUploader = ({ video, videoLink, onVideoUpload, onVideoLinkChange, onRemoveVideo, sessionId }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  
  const handleVideoUpload = async (file) => {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      toast.error("Invalid video format. Please use MP4, MOV, AVI, or WebM.");
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error("Video too large. Maximum size is 500MB.");
      return;
    }
    
    setUploading(true);
    setProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/photos/upload-video?session_id=${sessionId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percent);
          }
        }
      );
      
      onVideoUpload(response.data);
      toast.success("Video uploaded successfully!");
    } catch (error) {
      logger.error("Video upload failed:", error);
      toast.error("Failed to upload video. Please try again.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };
  
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4">
      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
        <Video className="w-5 h-5 text-[#D4AF37]" />
        Property Walkthrough Video (Optional)
      </h4>
      
      {video ? (
        <div className="flex items-center gap-3 p-3 bg-[#141414] border border-[#D4AF37]/30">
          <Video className="w-8 h-8 text-[#D4AF37]" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm truncate">{video.filename}</p>
            <p className="text-[#666] text-xs">{(video.size / 1024 / 1024).toFixed(1)} MB</p>
          </div>
          <button
            onClick={onRemoveVideo}
            className="p-1 hover:bg-red-500/20 rounded"
            type="button"
          >
            <X className="w-5 h-5 text-red-400" />
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-white/10 p-6 text-center cursor-pointer hover:border-[#D4AF37]/50 transition-colors"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,.mov,.avi,.webm"
              onChange={(e) => e.target.files?.[0] && handleVideoUpload(e.target.files[0])}
              className="hidden"
            />
            {uploading ? (
              <div>
                <div className="w-12 h-12 mx-auto mb-2 rounded-full border-4 border-[#D4AF37] border-t-transparent animate-spin" />
                <p className="text-[#D4AF37]">Uploading... {progress}%</p>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto mb-2 text-[#666]" />
                <p className="text-[#A3A3A3] text-sm">Click to upload video</p>
                <p className="text-[#666] text-xs mt-1">MP4, MOV, AVI, WebM (max 500MB / 3 min)</p>
              </>
            )}
          </div>
          
          <div className="text-center text-[#666] text-sm">— OR —</div>
          
          <div>
            <label className="block text-[#A3A3A3] text-sm mb-2 flex items-center gap-2">
              <Link className="w-4 h-4" />
              Paste a link (YouTube, Google Drive, Dropbox)
            </label>
            <input
              type="text"
              value={videoLink}
              onChange={(e) => onVideoLinkChange(e.target.value)}
              placeholder="https://..."
              className="w-full input-dark px-4 py-3 text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Photo Upload Helpers ============

const validatePhotoFile = (file, totalPhotos) => {
  if (totalPhotos >= MAX_PHOTOS) {
    toast.error(`Maximum ${MAX_PHOTOS} photos allowed.`);
    return false;
  }
  
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    toast.error("Invalid file type. Please use JPG, PNG, or HEIC.");
    return false;
  }
  
  if (file.size > MAX_IMAGE_SIZE) {
    toast.error("File too large. Maximum size is 10MB.");
    return false;
  }
  
  return true;
};

const createTempPhoto = (file, category) => {
  const previewUrl = URL.createObjectURL(file);
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  return {
    id: tempId,
    category,
    filename: file.name,
    previewUrl,
    uploading: true
  };
};

const uploadPhotoToServer = async (file, category, sessionId) => {
  const formDataUpload = new FormData();
  formDataUpload.append('file', file);
  
  const response = await axios.post(
    `${API}/photos/upload?category=${category}&session_id=${sessionId}`,
    formDataUpload,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  
  return response.data;
};

// ============ Main PropertyPhotosStep component ============

const PropertyPhotosStep = ({ formData, onPhotosChange, sessionId }) => {
  const [photos, setPhotos] = useState(formData.photos || []);
  const [video, setVideo] = useState(formData.video_storage_path ? { storage_path: formData.video_storage_path } : null);
  const [videoLink, setVideoLink] = useState(formData.video_link || "");
  const [isUploading, setIsUploading] = useState(false);
  
  const totalPhotos = photos.length;
  const isVacantLand = formData.property_type === 'Vacant Land';
  
  // Cleanup blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      photos.forEach(photo => {
        if (photo.previewUrl && photo.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(photo.previewUrl);
        }
      });
    };
  }, [photos]);
  
  // Update parent when photos change
  const updateFormData = useCallback((newPhotos, newVideo, newVideoLink) => {
    onPhotosChange({
      photos: newPhotos,
      video_storage_path: newVideo?.storage_path || "",
      video_link: newVideoLink
    });
  }, [onPhotosChange]);
  
  const handlePhotoUpload = async (file, category) => {
    if (!validatePhotoFile(file, totalPhotos)) {
      return;
    }
    
    const tempPhoto = createTempPhoto(file, category);
    const newPhotos = [...photos, tempPhoto];
    setPhotos(newPhotos);
    setIsUploading(true);
    
    try {
      const uploadedData = await uploadPhotoToServer(file, category, sessionId);
      
      // Replace temp photo with uploaded photo
      const uploadedPhoto = {
        ...uploadedData,
        previewUrl: tempPhoto.previewUrl
      };
      
      const updatedPhotos = newPhotos.map(p => 
        p.id === tempPhoto.id ? uploadedPhoto : p
      );
      
      setPhotos(updatedPhotos);
      updateFormData(updatedPhotos, video, videoLink);
      
    } catch (error) {
      logger.error("Photo upload failed:", error);
      toast.error("Failed to upload photo. Please try again.");
      URL.revokeObjectURL(tempPhoto.previewUrl);
      setPhotos(photos);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleRemovePhoto = (photoId) => {
    // Find the photo to get its previewUrl for cleanup
    const photoToRemove = photos.find(p => p.id === photoId);
    if (photoToRemove?.previewUrl && photoToRemove.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(photoToRemove.previewUrl);
    }
    
    const newPhotos = photos.filter(p => p.id !== photoId);
    setPhotos(newPhotos);
    updateFormData(newPhotos, video, videoLink);
  };
  
  const handleVideoUpload = (uploadedVideo) => {
    setVideo(uploadedVideo);
    updateFormData(photos, uploadedVideo, "");
    setVideoLink("");
  };
  
  const handleVideoLinkChange = (link) => {
    setVideoLink(link);
    setVideo(null);
    updateFormData(photos, null, link);
  };
  
  const handleRemoveVideo = () => {
    setVideo(null);
    updateFormData(photos, null, videoLink);
  };
  
  // Filter categories based on property type
  const relevantCategories = isVacantLand 
    ? PHOTO_CATEGORIES.filter(c => ['other', 'damaged_areas'].includes(c.id) || c.id.includes('front') || c.id.includes('back') || c.id.includes('side'))
    : PHOTO_CATEGORIES;
  
  return (
    <div className="space-y-6 animate-fade-in" data-testid="property-photos-step">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Camera className="w-5 h-5 text-[#D4AF37]" />
        Property Photos
        <span className="text-[#A3A3A3] text-sm font-normal">(Recommended)</span>
      </h2>
      
      {/* Encouragement message */}
      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4">
        <p className="text-[#D4AF37] text-sm leading-relaxed">
          <strong>The more photos you provide, the more accurate your cash offer can be.</strong> In many cases, photos allow us to provide a stronger preliminary offer before scheduling a visit.
        </p>
      </div>
      
      {/* Info box */}
      <div className="flex items-start gap-3 text-sm text-[#A3A3A3]">
        <AlertCircle className="w-5 h-5 text-[#666] flex-shrink-0 mt-0.5" />
        <p>
          Uploading photos helps us evaluate your property more accurately and may allow us to make a stronger cash offer without needing an immediate site visit.
        </p>
      </div>
      
      {/* Photo count indicator */}
      <div className="flex items-center justify-between">
        <span className="text-[#A3A3A3] text-sm">
          Photos uploaded: <span className="text-white font-medium">{totalPhotos}</span> / {MAX_PHOTOS}
        </span>
        {totalPhotos >= MAX_PHOTOS && (
          <span className="text-red-400 text-sm">Maximum reached</span>
        )}
      </div>
      
      {/* Photo categories grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {relevantCategories.map((category) => (
          <CategoryUploader
            key={category.id}
            category={category}
            photos={photos}
            onUpload={handlePhotoUpload}
            onRemove={handleRemovePhoto}
            isUploading={isUploading}
            sessionId={sessionId}
          />
        ))}
      </div>
      
      {/* Video uploader */}
      <VideoUploader
        video={video}
        videoLink={videoLink}
        onVideoUpload={handleVideoUpload}
        onVideoLinkChange={handleVideoLinkChange}
        onRemoveVideo={handleRemoveVideo}
        sessionId={sessionId}
      />
      
      {/* Privacy notice */}
      <div className="bg-[#0A0A0A] border border-white/10 p-4 flex items-start gap-3">
        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
        <p className="text-[#A3A3A3] text-sm">
          <strong className="text-white">Privacy Notice:</strong> Your photos are used only to evaluate your property and prepare the most accurate cash offer possible. They will never be sold or shared with third parties.
        </p>
      </div>
    </div>
  );
};

export default PropertyPhotosStep;
